import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarIcon from "../../../assets/icons/calender-icon";
import axios from "axios";
import { ENDPOINTS } from "../../../js/api/_config";

const ScheduleModal = ({
  open,
  setOpen,
  scheduleModalProps,
  onSchedule,
  loading,
  setLoading,
}) => {
  const roundUpTo15Min = (date) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    const remainder = minutes % 15;

    if (remainder !== 0) {
      rounded.setMinutes(minutes + (15 - remainder));
    }

    rounded.setSeconds(0);
    rounded.setMilliseconds(0);

    return rounded;
  };

  const roundUpAndAdd15Min = (date) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    const remainder = minutes % 15;

    if (remainder !== 0) {
      rounded.setMinutes(minutes + (15 - remainder));
    }

    rounded.setMinutes(rounded.getMinutes() + 15);

    rounded.setSeconds(0);
    rounded.setMilliseconds(0);

    return rounded;
  };

  const now = roundUpTo15Min(new Date());
  const endNow = roundUpAndAdd15Min(new Date());
  const [startDate, setStartDate] = useState(now);
  const [endDate, setEndDate] = useState();
  const [locationId, setLocationId] = useState(null);
  const [notes, setNotes] = useState("");

  const [location, setLocation] = useState([]);
  const [error, setErrors] = useState({});

  const datePickerStartDateRef = useRef(null);
  const datePickerEndDateRef = useRef(null);

  useEffect(() => {
    if (startDate) {
      setEndDate(null);
    }
  }, [startDate]);

  const validate = () => {
    const newErrors = {};

    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!endDate) {
      newErrors.endDate = "End date is required";
    } else if (startDate && endDate < startDate) {
      newErrors.endDate = "End date cannot be before start date";
    }

    if (!locationId) {
      newErrors.locationId = "Study location is required";
    }

    if (notes.length > 500) {
      newErrors.notes = "Notes cannot exceed 500 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const fetchLocation = async () => {
    await axios
      .get(ENDPOINTS.TRIALS.GET_ECLINPRO_LOCATIONS, {
        params: { trialID: scheduleModalProps.trialID },
      })
      .then((res) => {
        if (res.data.success) {
          setLocation(res.data.data);
        }
      });
  };

  function convertToSystemTimezone(utcDateString) {
    const date = new Date(utcDateString);

    const pad = (n) => String(n).padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hour = pad(date.getHours());
    const minute = pad(date.getMinutes());
    const second = pad(date.getSeconds());

    // timezone offset
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const offsetHour = pad(Math.floor(absOffset / 60));
    const offsetMinute = pad(absOffset % 60);

    return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHour}:${offsetMinute}`;
  }

  const onScheduleHandler = () => {
    if (!validate()) return;
    console.log("current start date onScheduleHandler", startDate)

    const formattedStartDate = convertToSystemTimezone(startDate);
    const formattedEndDate = convertToSystemTimezone(endDate);

    onSchedule({
      ...scheduleModalProps,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      locationId,
      notes,
    });
  };

  useEffect(() => {
    if (scheduleModalProps.trialID) {
      fetchLocation();
    }
  }, [scheduleModalProps.trialID]);

  if (!open) return null;

  const inputClass = `
  border_color border text-base rounded-lg block w-full p-0 appearance-none 
   text-base rounded-lg block w-full p-2 h-12 appearance-none max-h-[46px]
  `;

  const getMinTime = () => {
    if (startDate && startDate.toDateString() === now.toDateString()) {
      return now;
    }
    return new Date(new Date().setHours(0, 0, 0, 0));
  };

  const getEndMinTime = () => {
    if (!startDate) {
      return now;
    }

    // Same day → end time cannot be before start time
    if (endDate && startDate.toDateString() === endDate.toDateString()) {
      return startDate;
    }

    // Today (but different from start date)
    if (endDate && endDate.toDateString() === now.toDateString()) {
      return now;
    }

    return new Date(new Date().setHours(0, 0, 0, 0));
  };
  const getMaxTime = () => new Date(new Date().setHours(23, 59, 59, 999));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-[750px] rounded-2xl bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Schedule:{" "}
            <span className="font-bold">{scheduleModalProps.patientName}</span>
          </h2>
          <button onClick={() => setOpen(false)}>
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Start Date */}
          <div>
            <label className="mb-3 block text-sm font-medium">
              Start Date/Time <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <DatePicker
                selected={startDate ?? null}
                onChange={(date) => {
                  setStartDate(date);
                  if (date && date > endDate) setEndDate(date);
                }}
                
                showTimeSelect
                minDate={now}
                minTime={getMinTime()}
                maxTime={getMaxTime()}
                timeIntervals={15}
                dateFormat="dd MMM yyyy, HH:mm"
                placeholderText="Select"
                popperPlacement="bottom-start"
                popperClassName="!z-50"
                autoComplete="off"
                showPopperArrow={false}
                calendarClassName="rounded-xl border shadow-lg"
                className={inputClass}
                ref={datePickerStartDateRef}
              />
              <div
                onClick={() => datePickerStartDateRef.current?.setOpen(true)}
              >
                <CalendarIcon
                  className="absolute right-3 top-1/2  -translate-y-1/2 text-[#278EFF]"
                  color="#278EFF"
                />
              </div>
            </div>

            {error.startDate && (
              <div className="mt-1">
                <p className="text-[#F95454] font-mont text-[14px] font-[500]">
                  {error.startDate}
                </p>
              </div>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="mb-3 block text-sm font-medium">
              End Date/Time <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {console.log('start date before chosing enddate => ', startDate)}
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                onCalendarOpen={() => {
                  if (!endDate) {
                    setEndDate(startDate ? startDate : now);
                  }
                }}
                showTimeSelect
                timeIntervals={15}
                minDate={startDate ? startDate : now}
                minTime={endDate ? getEndMinTime() : getMinTime()}
                maxTime={getMaxTime()}
                dateFormat="dd MMM yyyy, HH:mm"
                placeholderText="Select"
                popperPlacement="bottom-start"
                popperClassName="!z-50"
                showPopperArrow={false}
                calendarClassName="rounded-xl border shadow-lg"
                className={inputClass}
                ref={datePickerEndDateRef}
              />
              <div onClick={() => datePickerEndDateRef.current?.setOpen(true)}>
                <CalendarIcon
                  className="absolute right-3 top-1/2  -translate-y-1/2 text-[#278EFF]"
                  color="#278EFF"
                />
              </div>
            </div>
            {error.endDate && (
              <div className="mt-1">
                <p className="text-[#F95454] font-mont text-[14px] font-[500]">
                  {error.endDate}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Study Location */}
        <div className="mt-3">
          <label className="mb-3 block text-sm font-medium">
            Study Location <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className={`${inputClass} appearance-none`}
            >
              <option value={""} selected disabled>
                Select Location
              </option>
              {location?.map((tz) => (
                <option
                  key={tz.eclinProLocationID}
                  value={tz.eclinProLocationID}
                >
                  {tz.displayName}
                </option>
              ))}
            </select>

            <span className="absolute right-3.5 top-[45%] h-5 w-5 transform -translate-y-1/2 pointer-events-none">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.9201 8.94995L13.4001 15.47C12.6301 16.24 11.3701 16.24 10.6001 15.47L4.08008 8.94995"
                  stroke="#278EFF"
                  strokeWidth="2"
                  stroke-miterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          {error.locationId && (
            <div className="mt-1">
              <p className="text-[#F95454] font-mont text-[14px] font-[500]">
                {error.locationId}
              </p>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mt-3">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            className="h-32 w-full rounded-xl border_color border p-4 text-sm outline-none"
          />

          <div className="flex justify-between items-center">
            {error.notes && (
              <div className="mt-1">
                <p className="text-[#F95454] font-mont text-[14px] font-[500]">
                  {error.notes}
                </p>
              </div>
            )}

            <div className="mt-1 text-xs text-gray-500">{notes.length}/500</div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            className="h-[40px] bg-[#278EFF] px-5 mr-3 py-1 text-base rounded-md border primary_btn_customized transition-all duration-500"
            onClick={() => {
              onScheduleHandler();
            }}
          >
            {loading ? <div className="mini-spinner"></div> : "Schedule"}
          </button>
          <button
            className="w-[103px] h-[40px] bg-white text-[#1A1A1B] border border-[#278EFF] rounded-lg transition-colors duration-200 hover:bg-[#278EFF] hover:text-white"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
